import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ShiftIncentiveData } from "@/pages/incentive/models/ShiftIncentiveData";
import type {
	CarrierChangeData,
	DmhEvent,
	PassChangeData,
	PushRateCrewData,
	RollingTimeData,
	ShiftReport,
	ShiftReportDelay,
	ShiftReportGanttDelay,
	ShiftReportHeat,
	ShiftReportProduction,
	ShiftReportProductionSummary,
	ShiftReportResponse,
	ShiftReportRollChange,
	ShiftReportStackerBundle,
	ShiftReportStats,
} from "../models/ShiftReportResponse";
import type { KPIData } from "@/shared/models/KpiData";

const moment = getMomentTz();

export const getShiftReport = async (
	params: { shiftId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
	];

	const resp = await apiService.callV2(
		"[M2].[GetShiftReport]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = get(
			resp,
			"data.tables",
			{},
		) as ShiftReportResponse["tables"];

		const shiftStatsRows = get(tables, "0.rows", []) as ShiftReportStats[];
		const shiftStats = shiftStatsRows.length > 0 ? shiftStatsRows[0] : null;

		const kpiDataRows = get(tables, "1.rows", []) as KPIData[];
		const kpiData = kpiDataRows.length > 0 ? kpiDataRows[0] : null;

		const ganttProduction = get(
			tables,
			"2.rows",
			[],
		) as ShiftReportProduction[];
		const formattedProduction = ganttProduction.map((gp) => {
			return {
				...gp,
				Start: gp.Start ? moment.utc(gp.Start).toDate() : null,
				End: gp.End ? moment.utc(gp.End).toDate() : null,
			};
		});
		const ganttDelays = get(tables, "3.rows", []) as ShiftReportGanttDelay[];
		const formattedDelays = ganttDelays.map((gd) => {
			return {
				...gd,
				Start: gd.Start ? moment.utc(gd.Start).toDate() : null,
				End: gd.End ? moment.utc(gd.End).toDate() : null,
			};
		});
		const heats = get(tables, "4.rows", []) as ShiftReportHeat[];
		const formattedHeats = heats.map((heat) => {
			return {
				...heat,
				Start: heat.Start ? moment.utc(heat.Start).toDate() : null,
				End: heat.End ? moment.utc(heat.End).toDate() : null,
			};
		});
		const delays = get(tables, "5.rows", []) as ShiftReportDelay[];
		const formattedShiftDelays = delays.map((d) => {
			return {
				...d,
				Start: d.Start ? moment.utc(d.Start).toDate() : null,
				End: d.End ? moment.utc(d.End).toDate() : null,
			};
		});

		const productionSummary = get(
			tables,
			"6.rows",
			[],
		) as ShiftReportProductionSummary[];
		const formattedProductionSummary = productionSummary.map((ps) => {
			return {
				...ps,
				Start: ps.Start ? moment.utc(ps.Start).toDate() : null,
				End: ps.End ? moment.utc(ps.End).toDate() : null,
			};
		});

		// Rolling Time Data (Result Set 8)
		const rollingTimeRows = get(tables, "7.rows", []) as RollingTimeData[];
		const rollingTimeRaw =
			rollingTimeRows.length > 0 ? rollingTimeRows[0] : null;
		const rollingTime = rollingTimeRaw
			? {
					...rollingTimeRaw,
					ExpectedRollOutTime: rollingTimeRaw.ExpectedRollOutTime
						? moment.utc(rollingTimeRaw.ExpectedRollOutTime).toDate()
						: null,
					ScheduledRollOutTime: rollingTimeRaw.ScheduledRollOutTime
						? moment.utc(rollingTimeRaw.ScheduledRollOutTime).toDate()
						: null,
				}
			: null;

		// Incentive Data (Result Set 14, index 13)
		const incentiveRows = get(tables, "13.rows", []) as ShiftIncentiveData[];
		const incentiveData: ShiftIncentiveData | null =
			incentiveRows.length > 0 ? incentiveRows[0] : null;

		// Uninstalled Carriers (Result Set 9)
		const uninstalledRaw = get(tables, "8.rows", []) as CarrierChangeData[];
		const uninstalledCarriers = uninstalledRaw.map((c) => ({
			...c,
			InstalledAt: c.InstalledAt ? moment.utc(c.InstalledAt).toDate() : null,
			UninstalledAt: c.UninstalledAt
				? moment.utc(c.UninstalledAt).toDate()
				: null,
		}));

		// Installed Carriers (Result Set 10)
		const installedRaw = get(tables, "9.rows", []) as CarrierChangeData[];
		const installedCarriers = installedRaw.map((c) => ({
			...c,
			InstalledAt: c.InstalledAt ? moment.utc(c.InstalledAt).toDate() : null,
			UninstalledAt: c.UninstalledAt
				? moment.utc(c.UninstalledAt).toDate()
				: null,
		}));

		// Pass Changes (Result Set 11)
		const passChangesRaw = get(tables, "10.rows", []) as PassChangeData[];
		const passChanges = passChangesRaw.map((pc) => ({
			...pc,
			Timestamp: pc.Timestamp ? moment.utc(pc.Timestamp).toDate() : null,
			InstalledAt: pc.InstalledAt ? moment.utc(pc.InstalledAt).toDate() : null,
			UninstalledAt: pc.UninstalledAt
				? moment.utc(pc.UninstalledAt).toDate()
				: null,
		}));

		// Roll Change Intervals (Result Set 12)
		const rollChangeRaw = get(tables, "11.rows", []) as ShiftReportRollChange[];
		const rollChangeIntervals = rollChangeRaw.map((rc) => ({
			...rc,
			Start: rc.Start ? moment.utc(rc.Start).toDate() : null,
			End: rc.End ? moment.utc(rc.End).toDate() : null,
		}));

		// D/M/H Events (Result Set 13)
		const dmhEventsRaw = get(tables, "12.rows", []) as DmhEvent[];
		const dmhEvents = dmhEventsRaw.map((e) => ({
			...e,
			Start: e.Start ? moment.utc(e.Start).toDate() : null,
			End: e.End ? moment.utc(e.End).toDate() : null,
		}));

		// Stacker Bundles (Result Set 15, index 14)
		const stackerBundles = get(
			tables,
			"14.rows",
			[],
		) as ShiftReportStackerBundle[];

		// Push Rate by Crew Data (Result Set 16, index 15)
		const pushRateData = get(tables, "15.rows", []) as PushRateCrewData[];

		return {
			ShiftStats: shiftStats,
			KpiData: kpiData,
			GanttProduction: formattedProduction,
			GanttDelays: formattedDelays,
			Heats: formattedHeats,
			Delays: formattedShiftDelays,
			ProductionSummary: formattedProductionSummary,
			RollingTime: rollingTime,
			IncentiveData: incentiveData,
			UninstalledCarriers: uninstalledCarriers,
			InstalledCarriers: installedCarriers,
			PassChanges: passChanges,
			RollChangeIntervals: rollChangeIntervals,
			DmhEvents: dmhEvents,
			StackerBundles: stackerBundles,
			PushRateData: pushRateData,
		} as ShiftReport;
	}

	throw new Error(resp.message || "Error fetching shift report data");
};
